<template>
  <Modal
    class="scoped"
    full-height
    :modal-style="{ maxWidth: '100vw' }"
    title="Bulk Apply Filter Sets"
  >
    <div class="card q-pa-lg column no-wrap full-height scroll">
      <div class="brief-table column">
        <div class="table-headers row">
          <div class="col-2 q-pa-md">ID</div>
          <div class="col q-pa-md">Group</div>
          <div class="col q-pa-md">Label</div>
          <div
            v-for="filterSet in filterSets"
            :key="filterSet.id"
            class="col q-pa-md"
          >
            {{ filterSet.label }}
          </div>
        </div>

        <div class="filters-headers row">
          <div class="col-2 q-pa-md" />
          <div class="col q-pa-md" />
          <div class="col q-pa-md" />
          <div
            v-for="filterSet in filterSets"
            :key="filterSet.id"
            class="col q-pa-md"
          >
            <QToggle
              v-model="selectAllToggles[filterSet.id]"
              @update:model-value="toggleAllBriefs(filterSet)"
            />
          </div>
        </div>

        <div class="table-body scroll">
          <div
            v-for="group in briefGroups"
            :key="group.id"
            class="group"
          >
            <div
              v-for="brief in group.briefs"
              :key="brief.id"
              class="row group-item table-row"
            >
              <div class="col-2 q-pa-md">
                {{ brief.id }}
              </div>
              <div class="col q-pa-md">
                <ColorIndicator
                  v-if="brief.group.color"
                  :color="brief.group.color"
                />
                {{ brief.group.label }}
              </div>
              <div class="col q-pa-md">
                {{ brief.label }}
              </div>
              <div
                v-for="filterSet in filterSets"
                :key="filterSet.id"
                class="col q-pa-md"
              >
                <QToggle v-model="selectedBriefs[brief.id][filterSet.id]" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #buttons>
      <BaseButton
        color="primary"
        label="Save"
        new-design
        @click="save"
      />
    </template>
  </Modal>
</template>

<script>
import { groupBy, uniqBy, xor } from "lodash-es";

import BaseButton from "shared/components/base/BaseButton.vue";
import Modal from "shared/components/core/modals/Modal.vue";
import ColorIndicator from "shared/components/core/various/ColorIndicator.vue";

export default {
  name: "BulkApplyFilterSetsModal",
  components: {
    Modal,
    BaseButton,
    ColorIndicator,
  },
  props: {
    briefs: {
      type: Array,
      required: true,
    },
    filterSets: {
      type: Array,
      required: true,
    },
  },
  emits: ["confirm", "close"],
  data() {
    return {
      selectedBriefs: this.setSelectedBriefs(),
      selectAllToggles: this.setSelectAllToggles(),
    };
  },
  computed: {
    briefGroups() {
      const briefsByGroup = groupBy(this.briefs, (brief) => brief.group.id);

      return uniqBy(
        this.briefs.map((brief) => brief.group),
        "id"
      )
        .map((briefGroup) => ({
          ...briefGroup,
          briefs: briefsByGroup[briefGroup.id].sort((briefA, briefB) =>
            briefA.position < briefB.position ? -1 : 1
          ),
        }))
        .sort((briefGroupA, briefGroupB) =>
          briefGroupA.position < briefGroupB.position ? -1 : 1
        );
    },
    enabledSelectedBriefs() {
      const enabledBriefs = [];

      this.briefs.forEach((brief) => {
        const selectedFilterIds = Object.keys(this.selectedBriefs[brief.id])
          .filter((key) => this.selectedBriefs[brief.id][key])
          .map((id) => Number(id));

        if (this.hasChanged(brief, selectedFilterIds)) {
          enabledBriefs.push({
            id: brief.id,
            filter_set_ids: selectedFilterIds,
          });
        }
      });

      return enabledBriefs;
    },
  },
  mounted() {
    this.selectedBriefs = this.setSelectedBriefs();
    this.selectAllToggles = this.setSelectAllToggles();
  },
  methods: {
    hasChanged(brief, newFilterIds) {
      const existingBriefs = brief.filter_set_ids || [];

      return xor(newFilterIds, existingBriefs).length;
    },
    setSelectedBriefs() {
      return this.briefs.reduce(
        (acc, brief) => ({
          ...acc,
          [brief.id]: this.setInitiallyEnabledFilterSets(brief),
        }),
        {}
      );
    },
    setInitiallyEnabledFilterSets(brief) {
      return this.filterSets.reduce(
        (obj, filterSet) => ({
          ...obj,
          [filterSet.id]: this.hasFilterSetEnabled(brief, filterSet.id),
        }),
        {}
      );
    },
    hasFilterSetEnabled(brief, filterSetId) {
      return (
        Boolean(brief.filter_set_ids) &&
        brief.filter_set_ids.includes(filterSetId)
      );
    },
    setSelectAllToggles() {
      return this.filterSets.reduce(
        (acc, filterSet) => ({
          ...acc,
          [filterSet.id]: this.briefs.every((brief) =>
            this.hasFilterSetEnabled(brief, filterSet.id)
          ),
        }),
        {}
      );
    },
    toggleAllBriefs(filterSet) {
      const newState = this.selectAllToggles[filterSet.id];

      Object.values(this.selectedBriefs).forEach((brief) => {
        Object.assign(brief, { [filterSet.id]: newState });
      });
    },
    save() {
      this.$emit("confirm", this.enabledSelectedBriefs);
      this.$emit("close");
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.container) {
  max-width: 80%;
}

.brief-table {
  border: 1px solid #ddd;
  max-height: 100%;

  .col,
  .col-1,
  .col-2 {
    background: white;
  }

  .table-headers,
  .filters-headers {
    border-bottom: 1px solid #ddd;
  }

  .table-headers {
    .col,
    .col-1,
    .col-2 {
      background: #ccc;
    }
  }

  .table-body {
    flex: 1;
  }
}

.group {
  min-height: 20px;
  border-bottom: 1px solid #ddd;

  &:last-child {
    border-bottom: none;
  }
}
</style>
